/**
 * Shared API utility for all Marketplace components.
 * Handles auth headers and error parsing.
 */

const API = "/api";

export function getAuthHeaders(body) {
  const token = localStorage.getItem('token');
  // A file upload must NOT carry our JSON content type: the browser writes
  // `multipart/form-data; boundary=…` itself and cannot once the header is already set, so the
  // server received an unparseable body and answered "Field required" on the file field. That
  // is why uploading a PDF into a session's intake forms failed (owner, Jun 2026).
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  return {
    ...(isForm ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function apiFetch(path, options = {}) {
  // Auto-append active vendor_id for multi-storefront support.
  // Defensively skip stale/garbage values that previously could send the
  // literal string "undefined" / "null" to the backend.
  let url = `${API}${path}`;
  const raw = localStorage.getItem('activeVendorId');
  const activeVendorId = (raw && raw !== 'undefined' && raw !== 'null') ? raw : null;
  if (!activeVendorId && raw) {
    // clean up corrupted localStorage
    localStorage.removeItem('activeVendorId');
  }
  const skipVendorId = options.skipVendorId === true;
  if (activeVendorId && !skipVendorId && path.startsWith('/vendor')) {
    const separator = url.includes('?') ? '&' : '?';
    url = `${url}${separator}vendor_id=${encodeURIComponent(activeVendorId)}`;
  }
  const res = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(options.body),
      ...(options.headers || {}),
    },
  });

  // A storefront id left in localStorage after that storefront was deleted (or its approval
  // withdrawn) makes every /vendor call 403, and screens that only render on success sat
  // there spinning. Retry once against their default storefront — and only forget the stored
  // id if that actually helps, so a 403 from anything else (tier gating, approval revoked)
  // doesn't wipe a working storefront selection as a side effect.
  if (res.status === 403 && activeVendorId && !skipVendorId && path.startsWith('/vendor')) {
    const retry = await fetch(`${API}${path}`, {
      ...options,
      headers: { ...getAuthHeaders(options.body), ...(options.headers || {}) },
    });
    if (retry.ok) {
      localStorage.removeItem('activeVendorId');
      return retry.json();
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Request failed' }));
    const detail = err.detail;
    let message;
    if (typeof detail === 'string') {
      message = detail;
    } else if (Array.isArray(detail)) {
      message = detail.map(d => d.msg || JSON.stringify(d)).join('; ');
    } else if (detail && typeof detail === 'object' && detail.message) {
      // Structured errors (e.g. the booking subscription gate) carry their own wording —
      // showing it beats "HTTP 402".
      message = detail.message;
    } else {
      message = `HTTP ${res.status}`;
    }
    const error = new Error(message);
    error.status = res.status;
    error.detail = detail;
    throw error;
  }
  // Anything that changed data lets the rest of the app know, so screens showing
  // counts (the Admin attention badges) can refresh themselves.
  if ((options.method || 'GET').toUpperCase() !== 'GET') {
    window.dispatchEvent(new CustomEvent('admin-data-changed'));
  }
  return res.json();
}

export { API };
